import { IPreferencesContent } from "@app/interfaces/IPreferencesContent";
import { Currency } from "@enums/Currency.enum";
import { DistanceMeasurement } from "@enums/measurements/DistanceMeasurement.enum";
import { FuelConsumptionMeasurement } from "@enums/measurements/FuelConsumptionMeasurement.enum";
import { TemperatureMeasurement } from "@enums/measurements/TemperatureMeasurement.enum";
import { VolumeMeasurement } from "@enums/measurements/VolumeMeasurement";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { MeasurementCategoryType } from "@reports/enums/ReportMeasurementType";
import { TranslationService } from "@services/translation.service";
import { ProductVendor } from "@videos/enums/CameraProductType";
import { IVideoEntry } from "@videos/models/IVideoEntry";
import { IVideoExtensionData } from "@videos/models/IVideoExtensionData";
import { SettingsName } from "../class/SettingsName";
import { ColumnDataType } from "../main/components/reports/enums/ColumnDataType";
import { SessionService } from "./session.service";

export enum UnitConversionType {
    Unknown = -1,
    Distance,
    Speed,
    FuelEfficiency,
    FuelCapacity,
    Currency,
    Temperature,
    Time
}

export interface IUnitConversionOptions {
    precision?: number;
    scaleLargeNumbers?: boolean;
    scaleLargeNumbersLimit?: number;
    dataType?: ColumnDataType;
    dontAppendUnit?: boolean;
}

export class UnitConversionService {

    private readonly kmToMiles = 0.621371;
    private readonly milesToKm = 1.609344;
    //private milesToMeter = 1609.344;
    private readonly mpgToKml = 0.35400619;
    private readonly kplToMpgUK = 2.824815; // UK  calculation
    private readonly kplToMpgUS = 2.352145; // US calculation

    private readonly mpgUKToKpl = 0.425144; // UK  calculation
    private readonly mpgUSToKpl = 0.354006; // US calculation

    private readonly litersToGallons = 0.219969;
    private readonly litersToUSGallons = 0.264172;
    private readonly gallonsToLiters = 4.54609;
    private readonly usGallonsToLiters = 3.78541;
    private readonly kplToMpl = 0.6214;
    private readonly kplToKpg = 3.78541178; // US calculation

    private userUnitIsImperial = true;

    private userPreferences: ICombinedPreferences;

    static $inject = ["$filter", "sessionService", "translationService"];

    constructor(
        private $filter: angular.IFilterService,
        private sessionService: SessionService,
        private readonly translationService: TranslationService
    ) {

        this.getAccountOptions();
    }

    smartConvert(input: string | number, unitType: UnitConversionType, options?: IUnitConversionOptions): string {
        if (input === null || input === undefined && unitType !== UnitConversionType.Temperature && unitType !== UnitConversionType.FuelCapacity) {
            return "0";
        }

        if (input === null || input === undefined && (unitType === UnitConversionType.Temperature || unitType === UnitConversionType.FuelCapacity)) {
            return;
        }

        if (!options) {
            options = {
                precision: 2,
            }
        }

        const parsedValue = parseFloat(input.toString());
        let convertedValue = this.convert(parsedValue, unitType, options.precision);

        let hasScaledValue = false;
        if (options.scaleLargeNumbers && convertedValue > options.scaleLargeNumbersLimit) {
            convertedValue = Math.round(convertedValue / 1000);
            hasScaledValue = true;
        }

        var conversionLabel = this.getConversionLabel(unitType);
        if (options.dataType == ColumnDataType.Percent) {
            conversionLabel = "%";
        }

        var displayValue = this.$filter("number")(convertedValue);

        if (hasScaledValue) {
            displayValue += "k";
        }

        var placeLabelAtSuffix = this.isLabelSuffix(unitType);

        if (placeLabelAtSuffix) {
            if (!options.dontAppendUnit) {
                displayValue += ` ${conversionLabel}`;
            }
        }
        else {
            displayValue = `${conversionLabel}${displayValue}`;
        }

        return displayValue;
    }

    convert(input: string | number, unitType: UnitConversionType, precision = 0, imperialToMetricConvert: boolean = false, customPref: number = -1, customPreferenceContent: IPreferencesContent = null) {

        if (typeof input === undefined || input == null) {
            console.warn(`Conversion cancelled, no input`);
            return 0;
        }

        if (!customPreferenceContent) {
            this.getAccountOptions();
        }

        const preferences: IPreferencesContent = (!!customPreferenceContent) ? customPreferenceContent : this.userPreferences;

        if (!preferences) {
            console.warn(`Conversion cancelled, no preferences determined`);
            return 0;
        }

        const parsedInput = parseFloat(input.toString());

        var returnValue = parsedInput; // Kilometers, Litres and Kilometers Per Liters not require conversion

        switch (unitType) {
            case UnitConversionType.Distance:
            case UnitConversionType.Speed:

                if (customPref == -1) {

                    if (preferences.DistanceMeasurement.Value == DistanceMeasurement.Miles) {
                        returnValue = returnValue * (imperialToMetricConvert ? this.milesToKm : this.kmToMiles);
                    }
                }
                else if (customPref == DistanceMeasurement.Miles) {
                    returnValue = returnValue * (imperialToMetricConvert ? this.milesToKm : this.kmToMiles)
                }

                break;

            case UnitConversionType.FuelCapacity:
                if (customPref == -1) {

                    switch (preferences?.VolumeMeasurement.Value) {

                        case VolumeMeasurement.UsGallon:
                            returnValue = returnValue * (imperialToMetricConvert ? this.usGallonsToLiters : this.litersToUSGallons);
                            break;

                        case VolumeMeasurement.Gallon:
                            returnValue = returnValue * (imperialToMetricConvert ? this.gallonsToLiters : this.litersToGallons);
                            break;

                        case VolumeMeasurement.Litre:
                        default:
                            break;
                    }
                }
                else {
                    switch (customPref) {
                        case VolumeMeasurement.UsGallon:

                            returnValue = returnValue * (imperialToMetricConvert ? this.usGallonsToLiters : this.litersToUSGallons);
                            break;

                        case VolumeMeasurement.Gallon:

                            returnValue = returnValue * (imperialToMetricConvert ? this.gallonsToLiters : this.litersToGallons);
                            break;

                        default:
                            break;
                    }
                }

                break;

            case UnitConversionType.FuelEfficiency:

                if (preferences?.FuelConsumptionMeasurement.Value == FuelConsumptionMeasurement.UnitedStatesMilesPerGallon) {
                    returnValue = returnValue * (imperialToMetricConvert ? this.mpgUSToKpl : this.kplToMpgUS);
                }

                if (preferences?.FuelConsumptionMeasurement.Value == FuelConsumptionMeasurement.MilesPerGallon) {
                    returnValue = returnValue * (imperialToMetricConvert ? this.mpgUKToKpl : this.kplToMpgUK);
                }

                if (preferences?.FuelConsumptionMeasurement.Value == FuelConsumptionMeasurement.LitresPer100Kilometres) {
                    returnValue = 100 / returnValue;
                }

                break;

            case UnitConversionType.Temperature:

                returnValue = (imperialToMetricConvert ? this.convertTemperatureForSaving(returnValue) : this.convertTemperature(returnValue));
                break;
        }

        return this.roundToXDecimalPlaces(returnValue, precision);
    }

    convertToDatabaseUnit(input: string | number, unitType: UnitConversionType, precision = 0) {
        this.getAccountOptions();

        // For custom purpose only Speed caught for conversion.
        // If this methods need to be used for other Types,
        // another cases are most likely needed to be created.
        switch (unitType) {
            case UnitConversionType.Speed:
                const shouldConvertToMetric = this.userPreferences.DistanceMeasurement.Value === DistanceMeasurement.Miles;
                return this.convert(input, unitType, precision, shouldConvertToMetric);

            default:
                return this.convert(input, unitType, precision);
        }
    }

    convertToUserUnit(input: string | number, unitType: UnitConversionType, precision = 0): number {
        this.getAccountOptions();

        return this.convert(input, unitType, precision);
    }

    convertToUserUnitWithLabel(input: string | number, unitType: UnitConversionType, precision = 0): string {
        this.getAccountOptions();

        const value = this.convert(input, unitType, precision);
        const label = this.getConversionLabel(unitType);

        return `${value} ${label}`;
    }

    roundToXDecimalPlaces(input: number, precision: number): number {
        const modifier = Math.pow(10, precision);
        return Math.round(input * modifier) / modifier;
    }

    convertUnitWithFormatting(input: string | number, suffixType: UnitConversionType = UnitConversionType.Speed, precision?: number): string {
        this.getAccountOptions();

        var precisionToUse = suffixType == UnitConversionType.FuelEfficiency ? 2 : 0 || UnitConversionType.Temperature ? 1 : 0;
        if (precision !== undefined) {
            precisionToUse = precision;
        }

        const convertedUnit = this.convertToUserUnit(input, suffixType, precisionToUse);
        return convertedUnit.toLocaleString();
    }

    convertUnitWithSuffix(input: string | number, suffixType: UnitConversionType = UnitConversionType.Speed, precision?: number): string {
        let convertedUnit = this.convertUnitWithFormatting(input, suffixType, precision);

        if (suffixType === UnitConversionType.Currency) {
            convertedUnit = `${this.getConversionLabel(suffixType)}${convertedUnit}`;
            return convertedUnit;
        }

        convertedUnit += suffixType === UnitConversionType.Temperature ? "" : " ";
        convertedUnit += this.getConversionLabel(suffixType);

        return convertedUnit;
    }

    convertMilesToMeters(milage: number) {
        return Math.round(milage * this.milesToKm * 1000);
    }

    convertKilometersToMiles(kilometers: number) {
        return kilometers / (this.milesToKm);
    }

    convertTemperature(temperature: number) {
        var temp = temperature;

        this.getAccountOptions();

        if (this.userPreferences.TemperatureMeasurement.Value == TemperatureMeasurement.Fahrenheit) {
            temp = Number(this.convertCelsiusToFahrenheit(temperature));
        }

        return temp;
    }

    convertTemperatureWithSuffix(temperature: number) {
        var convertedTemp = this.convertTemperature(temperature).toFixed(1).toString();

        convertedTemp += this.getConversionLabel(UnitConversionType.Temperature);

        return convertedTemp;
    }

    convertTemperatureForSaving(temperature: number) {
        var temp = temperature;

        this.getAccountOptions();

        if (this.userPreferences.TemperatureMeasurement.Value == TemperatureMeasurement.Fahrenheit) {
            temp = Number(this.convertFahrenheitToCelsius(temperature));
        }

        return temp;
    }

    convertMpgToKml(milage: number) {
        return Math.round(milage * this.mpgToKml);
    }

    convertCelsiusToFahrenheit(celsius: number) {
        var fahren = celsius * 9 / 5 + 32;

        return fahren.toFixed(2);
    }

    convertFahrenheitToCelsius(fahrenheit: number) {
        return (fahrenheit - 32) * 5 / 9;
    }

    getConversionType(suffixType: string): UnitConversionType {
        suffixType = suffixType.toLowerCase();

        if (suffixType === "distance") {
            return UnitConversionType.Distance;
        }
        else if (suffixType === "speed") {
            return UnitConversionType.Speed;
        }
        else if (suffixType === "fuel") {
            return UnitConversionType.FuelEfficiency;
        }
        else if (suffixType === "capacity") {
            return UnitConversionType.FuelCapacity;
        }
        else if (suffixType === "temperature") {
            return UnitConversionType.Temperature;
        }
        else if (suffixType === "currency") {
            return UnitConversionType.Currency;
        }
        else if (suffixType === "time") {
            return UnitConversionType.Time;
        }
        else if (suffixType === "none") {
            return UnitConversionType.Unknown;
        }

        // Default
        return UnitConversionType.Distance;
    }

    getConversionLabel(conversionType: UnitConversionType = UnitConversionType.Speed, customPreferenceContent: IPreferencesContent = null): string {
        return this.getConversionLabelWithUnitOption(conversionType, customPreferenceContent);
    }

    isLabelSuffix(conversionType: UnitConversionType): boolean {
        if (conversionType == UnitConversionType.Currency) {
            return false;
        }

        return true;
    }

    getConversionLabelWithUnitOption(suffixType: UnitConversionType, customPreferenceContent: IPreferencesContent = null): string {

        if (!customPreferenceContent) {
            this.getAccountOptions();
        }

        const preferences: IPreferencesContent = (!!customPreferenceContent) ? customPreferenceContent : this.userPreferences;

        switch (suffixType) {
            case UnitConversionType.Speed:
                return preferences?.DistanceMeasurement.Value === DistanceMeasurement.Miles
                    ? "mph"
                    : "km/h";

            case UnitConversionType.Distance:
                return preferences?.DistanceMeasurement.Value === DistanceMeasurement.Miles
                    ? this.translationService.t("unitConversionService.miles")
                    : "km";

            case UnitConversionType.Temperature:
                return preferences?.TemperatureMeasurement.Value === TemperatureMeasurement.Celsius
                    ? "°C"
                    : "°F";

            case UnitConversionType.FuelCapacity:
                return preferences?.VolumeMeasurement.Value === VolumeMeasurement.Gallon || preferences?.VolumeMeasurement.Value === VolumeMeasurement.UsGallon
                    ? "gal."
                    : this.translationService.t("unitConversionService.litres");
            case UnitConversionType.FuelEfficiency:
                switch (preferences?.FuelConsumptionMeasurement.Value) {
                    case FuelConsumptionMeasurement.MilesPerGallon:
                    case FuelConsumptionMeasurement.UnitedStatesMilesPerGallon:
                        return "mpg";
                    case FuelConsumptionMeasurement.LitresPer100Kilometres:
                        return "l/100km";
                    case FuelConsumptionMeasurement.KilometresPerLitre:
                        return "kpl";
                }
            case UnitConversionType.Currency:
                switch (preferences?.Currency.Value) {
                    case Currency.EUR:
                        return "€";
                    case Currency.GBP:
                        return "£";
                    case Currency.USD:
                        return "$";
                    default:
                        return "£";
                }

            case UnitConversionType.Time:
                return this.translationService.t("unitConversionService.minutes");

            default:
                return "";
        }
    }

    convertMeasurementCategoryIntoUnitConversionType(measurementType: MeasurementCategoryType): UnitConversionType {
        switch (measurementType) {
            case MeasurementCategoryType.Distance:
                return UnitConversionType.Distance;

            case MeasurementCategoryType.Fuel:
                return UnitConversionType.FuelCapacity;

            case MeasurementCategoryType.FuelEfficiency:
                return UnitConversionType.FuelEfficiency;

            case MeasurementCategoryType.Currency:
                return UnitConversionType.Currency;

            case MeasurementCategoryType.Speed:
                return UnitConversionType.Speed;

            default:
                return UnitConversionType.Unknown;
        }
    }

    convertExtensionDataIntoGForce(videoEntry: IVideoEntry, data: IVideoExtensionData): IVideoExtensionData {
        if (!data || !data.VideoAccelerometerData) {
            return;
        }

        const accelometerData = data.VideoAccelerometerData.map(data => {
            const newData = { ...data };

            newData.X = this.convertAccelerationToGUnit(data.X, videoEntry.ProductGroupId);
            newData.Y = this.convertAccelerationToGUnit(data.Y, videoEntry.ProductGroupId) * -1; // This is done to make left / right feel more obvious visually
            newData.Z = this.convertAccelerationToGUnit(data.Z, videoEntry.ProductGroupId);

            // IT cameras swap lat / lng (aka X and Y around)
            if (videoEntry.ProductGroupId == ProductVendor.IntelligentTelematics) {
                const x = newData.Y;
                const y = newData.X;

                // IT Vision camera seems to invert their long figure...
                newData.X = y;
                newData.Y = x;
            }

            return newData;
        });

        return { ...data, VideoAccelerometerData: accelometerData };
    }

    convertAccelerationToGUnit(accelerationMSec: number, cameraType: ProductVendor): number {
        var gunit = accelerationMSec / 100;

        // RTL cameras are already in G units so no need for an extra conversion
        if (cameraType != ProductVendor.RTL) {
            // May need to divide by 1000 for IT?

            // 1 g- unit is equal 980.66499997877 gal (cm / s�)
            //var accelerationCmSec = accelerationMSec * 100;
            var gunit = gunit / 9.8066499997877;
        }

        return this.roundToXDecimalPlaces(gunit, 2);
    }

    getAccountOptions(): ICombinedPreferences {
        if (!this.userPreferences) {
            this.userPreferences = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);
        }

        return this.userPreferences;
    }

    isLabelMetric(label: string): boolean {
        const metricLabels: string[] = [
            "km",
            "kpl",
            "km/h",
            `${this.translationService.t("unitConversionService.litres")}`
        ];

        return metricLabels.includes(label);
    }
}
